/**
 * SOW page chrome (header / footer / page numbers)
 * ────────────────────────────────────────────────
 * Pure read/write helpers for the running header & footer of the body page
 * section - the parts of a SOW that are NOT logical sections and so were never
 * editable in the old workbench. Edits target a single text paragraph and a
 * dedicated page-number paragraph, preserving any logo tables/images already in
 * the header/footer.
 */

import type {
  DocumentContract,
  HeaderFooterDefinition,
  ParagraphChild,
  ParagraphDefinition,
  TextRunDefinition,
} from '@protoss/doc';

export interface PageChrome {
  headerText: string;
  footerText: string;
  showPageNumbers: boolean;
}

/** A text run is the only ParagraphChild without a discriminating `type` tag. */
function isTextRun(run: ParagraphChild): run is TextRunDefinition {
  return !('type' in run);
}

function isParagraph(child: HeaderFooterDefinition['children'][number]): child is ParagraphDefinition {
  return child.type === 'paragraph';
}

/** The first text-bearing paragraph (the "running text"), skipping logo tables/images. */
function firstTextParagraphIndex(hf: HeaderFooterDefinition | undefined): number {
  if (!hf) return -1;
  return hf.children.findIndex(
    (c) => isParagraph(c) && c.children.some((r) => isTextRun(r) && !hasPageField([c]))
  );
}

function hasPageField(children: HeaderFooterDefinition['children']): boolean {
  return children.some(
    (c) => isParagraph(c) && c.children.some((r) => 'type' in r && r.type === 'pageField')
  );
}

function paragraphText(p: ParagraphDefinition | undefined): string {
  if (!p) return '';
  return p.children
    .filter(isTextRun)
    .map((r) => r.text ?? '')
    .join('');
}

export function readPageChrome(contract: DocumentContract, bodyIndex: number): PageChrome {
  const props = contract.sections?.[bodyIndex]?.properties;
  const header = props?.headers?.default;
  const footer = props?.footers?.default;
  const hi = firstTextParagraphIndex(header);
  const fi = firstTextParagraphIndex(footer);
  return {
    headerText: hi >= 0 ? paragraphText(header!.children[hi] as ParagraphDefinition) : '',
    footerText: fi >= 0 ? paragraphText(footer!.children[fi] as ParagraphDefinition) : '',
    showPageNumbers: hasPageField(footer?.children ?? []),
  };
}

function clone(contract: DocumentContract): DocumentContract {
  return JSON.parse(JSON.stringify(contract)) as DocumentContract;
}

/** Set the running text of a header/footer, preserving logo tables/page-number rows. */
function setRunningText(hf: HeaderFooterDefinition, text: string): void {
  const idx = firstTextParagraphIndex(hf);
  const para: ParagraphDefinition = { type: 'paragraph', children: [{ text }] };
  if (idx >= 0) {
    if (text.trim()) hf.children[idx] = para;
    else hf.children.splice(idx, 1); // clearing the text removes the running-text paragraph
  } else if (text.trim()) {
    hf.children.unshift(para);
  }
}

function setPageNumbers(footer: HeaderFooterDefinition, on: boolean): void {
  // Drop any existing page-number paragraph.
  footer.children = footer.children.filter(
    (c) => !(isParagraph(c) && c.children.some((r) => 'type' in r && r.type === 'pageField'))
  );
  if (on) {
    footer.children.push({
      type: 'paragraph',
      alignment: 'center',
      children: [
        { text: 'Page ' },
        { type: 'pageField', field: 'current', fallback: '1' },
      ],
    } as ParagraphDefinition);
  }
}

export function writePageChrome(
  contract: DocumentContract,
  bodyIndex: number,
  patch: Partial<PageChrome>
): DocumentContract {
  const next = clone(contract);
  const section = next.sections?.[bodyIndex];
  if (!section) return next;
  const props = (section.properties = section.properties ?? {});

  if (patch.headerText !== undefined) {
    const headers = (props.headers = props.headers ?? {});
    headers.default = headers.default ?? { children: [] };
    setRunningText(headers.default, patch.headerText);
  }
  if (patch.footerText !== undefined || patch.showPageNumbers !== undefined) {
    const footers = (props.footers = props.footers ?? {});
    footers.default = footers.default ?? { children: [] };
    if (patch.footerText !== undefined) setRunningText(footers.default, patch.footerText);
    if (patch.showPageNumbers !== undefined) setPageNumbers(footers.default, patch.showPageNumbers);
  }
  return next;
}
